import React from 'react';
import StatsCard from '../components/StatsCard.jsx';
import Card from '../components/Card.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';

export default function Dashboard({ stats, isLoading, onNavigate }) {
  if (isLoading || !stats) {
    return <LoadingSpinner />;
  }

  // Calculate trends
  const purchaseRate = stats.total_generated > 0 
    ? Math.round((stats.total_purchased / stats.total_generated) * 100) 
    : 0;
  const usageRate = stats.total_purchased > 0 
    ? Math.round((stats.total_used / stats.total_purchased) * 100) 
    : 0;
  const fraudPrevention = stats.total_generated - stats.total_purchased;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Welcome back! Here's your monthly overview</p>
      </div>

      <div className="stats-grid">
        <StatsCard 
          label="Stamps Generated"
          value={stats.total_generated || 0}
          color="blue"
          subtext="This month"
        />
        <StatsCard 
          label="Stamps Purchased"
          value={stats.total_purchased || 0}
          color="pink"
          subtext={`${purchaseRate}% conversion`}
        />
        <StatsCard 
          label="Successfully Used"
          value={stats.total_used || 0}
          color="green"
          subtext={`${usageRate}% of purchased`}
        />
        <StatsCard 
          label="Fraud Prevention"
          value={fraudPrevention}
          color="purple"
          subtext="Unpaid stamps"
        />
      </div>

      <div className="two-col">
        <Card title="Performance Metrics" subtitle="Conversion funnel analysis">
          <div style={{ padding: '20px' }}>
            {/* Visual Progress Bars */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Purchase Conversion</span>
                <span style={{ fontSize: '14px', color: 'var(--pink)', fontWeight: '600' }}>{purchaseRate}%</span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                background: 'var(--bg-tertiary)', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${purchaseRate}%`, 
                  height: '100%', 
                  background: 'var(--pink)',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Usage Rate</span>
                <span style={{ fontSize: '14px', color: 'var(--green)', fontWeight: '600' }}>{usageRate}%</span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                background: 'var(--bg-tertiary)', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${usageRate}%`, 
                  height: '100%', 
                  background: 'var(--green)',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>

            {/* Funnel Visualization */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>Conversion Funnel</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ 
                  background: 'var(--blue)', 
                  padding: '16px', 
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '500' }}>Generated</span>
                  <span style={{ fontSize: '20px', fontWeight: '700' }}>{stats.total_generated}</span>
                </div>
                <div style={{ 
                  background: 'var(--pink)', 
                  padding: '16px', 
                  borderRadius: '8px',
                  width: `${purchaseRate}%`,
                  minWidth: '50%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '500' }}>Purchased</span>
                  <span style={{ fontSize: '20px', fontWeight: '700' }}>{stats.total_purchased}</span>
                </div>
                <div style={{ 
                  background: 'var(--green)', 
                  padding: '16px', 
                  borderRadius: '8px',
                  width: `${usageRate * (purchaseRate / 100)}%`,
                  minWidth: '40%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '500' }}>Used</span>
                  <span style={{ fontSize: '20px', fontWeight: '700' }}>{stats.total_used}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card title="Financial Overview">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '8px' }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Revenue This Month
              </div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: 'var(--green)' }}>
                ${((stats.total_purchased || 0) * 0.50).toFixed(2)}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                $0.50 per purchased stamp
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Potential Revenue (Unused)
              </div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: 'var(--orange)' }}>
                ${(((stats.total_purchased || 0) - (stats.total_used || 0)) * 0.50).toFixed(2)}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {(stats.total_purchased || 0) - (stats.total_used || 0)} stamps ready
              </div>
            </div>

            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Fraud Prevention Savings
              </div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: 'var(--purple)' }}>
                ${(fraudPrevention * 0.50).toFixed(2)}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {fraudPrevention} unpaid codes blocked
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card 
  title="Quick Actions" 
  subtitle="Manage your stamps and settings"
>
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', padding: '8px' }}>
    <button className="btn btn-primary" style={{ justifyContent: 'center' }} onClick={() => onNavigate('stamps')}>
      📊 View All Stamps
    </button>
    <button className="btn btn-secondary" style={{ justifyContent: 'center' }} onClick={() => onNavigate('stats')}>
      📈 Detailed Statistics
    </button>
    <button className="btn btn-secondary" style={{ justifyContent: 'center' }} onClick={() => onNavigate('api-keys')}>
      🔑 Manage API Keys
    </button>
    <button className="btn btn-secondary" style={{ justifyContent: 'center' }} onClick={() => onNavigate('security')}>
      ⚙️ Account Settings
    </button>
  </div>
</Card>
    </div>
  );
}